const retryTimerMap = new WeakMap()

export const autoTranslateDirective = {
  mounted(el) {
    if (el.classList.contains('dont-translate')) {
      return
    }

    runTranslationPass(el)
    scheduleRetries(el)
  },

  updated(el) {
    runTranslationPass(el)
    scheduleRetries(el)
  },

  unmounted(el) {
    const timerIds = retryTimerMap.get(el) || []
    timerIds.forEach((id) => clearTimeout(id))
    retryTimerMap.delete(el)
  },
}

function runTranslationPass(el) {
  processTextNodes(el)
  processAttributes(el)
}

function scheduleRetries(el) {
  const existingTimerIds = retryTimerMap.get(el) || []
  existingTimerIds.forEach((id) => clearTimeout(id))

  const timerIds = [300, 900, 1800].map((delay) =>
    setTimeout(() => runTranslationPass(el), delay),
  )

  retryTimerMap.set(el, timerIds)
}

export function processTextNodes(el) {
  const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT, {
    acceptNode: (node) => {
      if (node.parentElement?.classList.contains('dont-translate')) {
        return NodeFilter.FILTER_REJECT
      }

      return NodeFilter.FILTER_ACCEPT
    },
  })

  const textNodes = []
  let node

  while ((node = walker.nextNode())) {
    textNodes.push(node)
  }

  textNodes.forEach((textNode) => {
    const text = normalizeText(textNode.textContent)

    if (text && shouldTranslate(text)) {
      translateText(textNode, text)
    }
  })
}

export function processAttributes(el) {
  const attributesToTranslate = ['placeholder', 'title', 'alt', 'aria-label']
  const elementsWithAttributes = el.querySelectorAll(
    attributesToTranslate.map((attr) => `[${attr}]`).join(','),
  )

  const allElements = [el, ...Array.from(elementsWithAttributes)]

  allElements.forEach((element) => {
    if (element.classList?.contains('dont-translate')) {
      return
    }

    attributesToTranslate.forEach((attr) => {
      const value = element.getAttribute(attr)
      const normalizedValue = normalizeText(value)

      if (normalizedValue && shouldTranslate(normalizedValue)) {
        const translated = translateTextValue(normalizedValue)
        const cleanTranslated = translated.replace(/[\u200B-\u200D\uFEFF]/g, '')
        const cleanValue = normalizedValue.replace(/[\u200B-\u200D\uFEFF]/g, '')

        if (cleanTranslated !== cleanValue) {
          element.setAttribute(attr, translated)
        }
      }
    })
  })
}

function translateText(textNode, text) {
  if (!window.tolgee || typeof window.tolgee.t !== 'function') {
    return
  }

  try {
    const translation = window.tolgee.t(text)

    if (translation && translation !== text) {
      textNode.textContent = translation
    }
  } catch (error) {
    console.error('Error translating text:', error)
  }
}

function normalizeText(value) {
  return (value || '')
    .replace(/[\u200B-\u200D\uFEFF]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
}

function translateTextValue(text) {
  if (!window.tolgee || typeof window.tolgee.t !== 'function') {
    return text
  }

  try {
    return window.tolgee.t(text) || text
  } catch (error) {
    console.error('Error translating text:', error)
    return text
  }
}

function shouldTranslate(text) {
  if (text.length < 2) {
    return false
  }

  if (/^\d+$/.test(text)) {
    return false
  }

  if (/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(text)) {
    return false
  }

  if (/^https?:\/\//.test(text)) {
    return false
  }

  const skipWords = [
    'ID',
    'API',
    'URL',
    'HTTP',
    'HTTPS',
    'JSON',
    'XML',
    'CSS',
    'HTML',
  ]

  if (skipWords.includes(text.toUpperCase())) {
    return false
  }

  return true
}
